/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActivityLogger } from './ActivityLogger';
import { CopilotCallbacks } from './CopilotCallbacks';
import {
  ExternalComm,
  ExternalCommuicationParentMessage
} from './ExternalComm';
import { CopilotUserManager } from './CopilotUserManager';
import { User } from './User';
import { FrameExternalComm } from './FrameExternalComm';
import { CopilotResponseTags } from './data/CopilotResponseTags';
import { CopilotUser } from './CopilotUser';
import { ActivityDefinition } from './data/ActivityDefinition';
import { SavedUserState } from '../../utils';

/**
 * Contains methods for talking to student app (game)
 * and for sending game information to teacher app dashboard
 **/
const API_VERSION = '1.0';
//from External
const START_ACTIVITY = 'startActivity';
const STOP_ACTIVITY = 'stopActivity';
const SET_WIDGET_ID = 'setWidgetId';
const SET_PAUSED = 'setPaused';
const ADD_USER = 'addUser';
const REMOVE_USER = 'removeUser';
const COMMAND_TO_WIDGET = 'commandToWidget';
//to External
const WIDGET_READY = 'CgsGames.onWidgetReady';
const SAVE_STATE = 'CgsGames.saveUserState';
const ACTIVITY_COMPLETE = 'CgsGames.onActivityComplete';
const MESSAGE_COMPLETE = 'CgsGames.onMessageComplete';
const SEND_COMMAND = 'CgsGames.commandToCopilot';
const MODULE_COMPLETE = 'CgsGames.moduleComplete';  // Include boolean of whether grade should be sent
const MODULE_SURVEY_CLICKED = 'CgsGame.moduleSurveyClicked'; // Include boolean of whether grade should be sent
const REVIEW_STUDENT_RESPONSES = 'CgsGames.reviewStudentResponses';
const PROBLEM_SET_START = 'CgsGames.logProblemSetStart';
const PROBLEM_SET_END = 'CgsGames.logProblemSetEnd';
const PROBLEM_RESULT = 'CgsGames.logProblemResult';

export class CopilotService implements ActivityLogger {
  // private  _completeCallbackOfGame : CgsUserResponse -> void;
  private _copilotProps: CopilotCallbacks;
  private _widgetId: string;
  private _activityDefinition: ActivityDefinition | null;

  //State
  private _externalComsEnabled: boolean;

  // Users
  private _userManager: CopilotUserManager;

  // External Communication
  private ec: ExternalComm;

  public ecEmulator: any;

  /**
   * Creates the Copilot Service.
   * NOTE: the Copilot Service ASSUMES you are caching to the database. If you do not, you will
   * not get any UserState from the Copilot Service (ie. during the startActivity call).
   * NOTE: Usernames are not currently set for any users. Contact the Copilot team if you need username data.
   */
  public constructor(
    copilotProps: CopilotCallbacks,
    ec?: ExternalComm,
    userManager?: CopilotUserManager
  ) {
    this._activityDefinition = null;
    this._externalComsEnabled = true;
    this._widgetId = 'NEW_WIDGET';

    this._copilotProps = copilotProps;

    this._userManager =
      userManager !== undefined ? userManager : new CopilotUserManager();

    this.ec = ec !== undefined ? ec : new FrameExternalComm();
    this.ecEmulator = {
      startActivity: this.startActivity.bind(this),
      stopActivity: this.stopActivity.bind(this),
      setPaused: this.setPaused.bind(this)
    };
  }

  /**
   *
   * Internal State
   *
   **/

  /**
   * Returns a flag indicating the state of outgoing external interface
   */
  public get externalComsEnabled(): boolean {
    return this._externalComsEnabled;
  }

  /**
   * Set the state of outgoing external interface
   */
  public set externalComsEnabled(value: boolean) {
    this._externalComsEnabled = value;
  }

  public get copilotProps(): CopilotCallbacks {
    return this._copilotProps;
  }

  public set copilotProps(value: CopilotCallbacks) {
    this._copilotProps = value;
  }
  /**
   *
   * Notifications of State
   *
   **/

  /**
   * Notifies the Copilot Service that activity is complete
   * @param datails - optional object to pass widget specific data
   */
  public onActivityComplete(details: any = {}): void {
    if (this._activityDefinition != null) {
      this.callExternal(ACTIVITY_COMPLETE, [
        this._activityDefinition.daid,
        details
      ]);
    }
  }

  /**
   * Notifies the Copilot Service that the widget is ready to recieve commands from the copilot.
   * @param datails - optional object to pass game specific data
   */
  public onWidgetReady(details: any = {}): void {
    // Setup user callbacks for the student app
    this.setupExternalInterface();

    //Tell external that we are ready, and our API version
    this.callExternal(WIDGET_READY, [API_VERSION, details]);
  }

  /**
   *
   * @param savedUserState
   */
  public saveUserState(savedUserState: SavedUserState): void {
    this.callExternal(SAVE_STATE, [JSON.stringify(savedUserState)]);
  }
  /**
   *
   * Responses
   *
   **/

  /**
   * Creates onMessageComplete call to Javascript
   * @param mid - message ID
   * @param success - whether message was successfully processed
   * @param data - return data from message processing
   */
  private onMessageComplete(mid: string, success: boolean, data: any): void {
    data = data != null ? data : {};
    this.callExternal(MESSAGE_COMPLETE, [mid, success, data]);
  }

  /**
   *
   * Result Set Calls
   *
   **/

  /**
   * Returns the daid (Dynamic Activity ID) of the current activity, if any.
   * Returns an empty string if there is no currently running activity
   */
  public get dynamicActivityId(): string {
    let result = '';
    if (this._activityDefinition != null) {
      result = this._activityDefinition.daid;
    }
    return result;
  }

  /**
   * Logs start of problem set
   * @param uid - User ID of the problem set. Each User may have only one active Problem Set at any given time.
   * @param psid - ID of a problem set.
   * @param problemCount - number of problems in this set (negative value denotes unknown number).
   * @param problemSetData - widget-specific data about problem set. Null is converted to {}.
   * @param details - optional data about this problem set. Null is converted to {}.
   */
  public logProblemSetStart(
    uid: string,
    psid: string,
    problemCount = -1,
    problemSetData: any = {},
    details: any = {}
  ): void {
    if (this.isUidValid(uid)) {
      this.callExternal(PROBLEM_SET_START, [
        uid,
        psid,
        problemCount,
        problemSetData,
        details
      ]);
    }
  }

  /**
   * Logs end of problem set
   * @param uid - User ID of the problem set.
   * @param problemSetData - widget-specific data about problem set. Null is converted to {}.
   * @param details - optional data about problem set. Null is converted to {}.
   */
  public logProblemSetEnd(
    uid: string,
    problemSetData: any = {},
    details: any = {}
  ): void {
    if (this.isUidValid(uid)) {
      this.callExternal(PROBLEM_SET_END, [uid, problemSetData, details]);
    }
  }

  /**
   * Logs a problem result
   * @param uid - User ID of the problem.
   * @param value - value in range [0, 1] representing the result
   *          0 - complete failure (0%)
   *          1 - complete success (100%)
   * @param problemPartList - optional list of concept strings that this problem tests. Null is converted to [].
   * @param problemData - widget-specific data about problem. Null is converted to {}.
   * @param details - optional data about this problem. Null is converted to {}.
   */
  public logProblemResult(
    uid: string,
    value: number,
    problemPartList: any[] | string = [],
    problemData: any = {},
    details: any = {}
  ): void {
    if (this.isUidValid(uid)) {
      this.callExternal(PROBLEM_RESULT, [
        uid,
        value,
        problemPartList,
        problemData,
        details
      ]);
    }
  }

  public logModuleComplete(uid: string, sendGrade: boolean) {
    if (this.isUidValid(uid)) {
      this.callExternal(MODULE_COMPLETE, [sendGrade]);
    }
  }

  public moduleSurveyClicked(uid: string) {
    if (this.isUidValid(uid)) {
      this.callExternal(MODULE_SURVEY_CLICKED, [true]);
    }
  }

  public requestReviewStudentResponses(uid: string) {
    if (this.isUidValid(uid)) {
      this.callExternal(REVIEW_STUDENT_RESPONSES, []);
    }
  }

  /**
   *
   * Generic
   *
   **/

  /**
   * Sends a generic message to Javascript as a JSON encoded string
   * @param command - name of command
   * @param args - JSON encodable object containing command parameters. Null is converted to {}
   */
  public commandToCopilot(command: string, args: any = null): void {
    args = args != null ? args : {};
    this.callExternal(SEND_COMMAND, [command, JSON.stringify(args)]);
  }

  /**
   *
   * External Interface Calls
   *
   **/

  /**
   * Sets up ExternalInterface by registering all necessary functions
   */
  private setupExternalInterface(): void {
    this.ec.setMessageCallback(START_ACTIVITY, this.startActivity.bind(this));
    this.ec.setMessageCallback(STOP_ACTIVITY, this.stopActivity.bind(this));
    this.ec.setMessageCallback(SET_WIDGET_ID, this.setWidgetId.bind(this));
    this.ec.setMessageCallback(SET_PAUSED, this.setPaused.bind(this));
    this.ec.setMessageCallback(
      COMMAND_TO_WIDGET,
      this.commandToWidget.bind(this)
    );
    this.ec.setMessageCallback(ADD_USER, this.addUser.bind(this));
    this.ec.setMessageCallback(REMOVE_USER, this.removeUser.bind(this));
  }

  /**
   * A wrapper to ExternalInterface.call
   * Does the check for ExternalInterface availability, and wraps the call in a try/catch,
   * so that the game does not crash if a method is missing in Javascript.
   * Adds gameId from flashVars as the first argument.
   * For further details, see calling functions. Their documentation can be found at
   * https://docs.google.com/a/cs.washington.edu/document/d/1xX2F8bIpGwQm6Bu4sbwgPdiUyMGum5hwj9JyDZPwftU/edit
   *
   * @param funcName - mothod name to call
   * @param ...args - method arguments to pass
   */
  private callExternal(funcName: string, args: any[]): void {
    //check that outside world exists and we are communicating with it
    if (this.externalComsEnabled) {
      try {
        args.unshift(this._widgetId);
        const msg: ExternalCommuicationParentMessage = {
          command: funcName,

          args: args
        };
        this.ec.sendMsgToParent(msg);
      } catch (e) {
        this.externalComsEnabled = false;
        const exceptionCallback = this._copilotProps.exceptionCallback;
        if (exceptionCallback != null) {
          exceptionCallback(false, e);
        }
      }
    }
  }

  /**
   * Logs a message in Javascript via console.log function
   * @param message - Message to log
   */
  private traceExternal(message: string): void {
    message = 'Copilot Service: ' + message;
    console.log(message);
  }

  /**
   *
   * Copilot Command Callbacks - Game Management
   *
   **/

  /**
   * Sets the id which the widget uses to identify itself to the copilot
   * @param mid - Message id of the request
   * @param widgetId - ID of this widget
   */
  private setWidgetId(args: any[]): void {
    const mid: string = args[0];
    const widgetId: string = args[1];
    this._widgetId = widgetId;
    this.onMessageComplete(mid, true, {});
  }

  /**
   * Starts the widget with given parameters. Attempts to end any currently running activity.
   * @param mid - Message id of the request
   * @param userList - list of user objects with folowing format {uid:string, userState:any}
   * @param activityDefinition - object containing activity data with the folowing format
   *          {cid: string; daid:string, lid:string, activityData:any}
   * @param details - object containing additional/optional parameters
   */
  private startActivity(args: any[]): void {
    const mid: string = args[0];
    const userList: any[] = args[1];
    const activityDefinition: ActivityDefinition = args[2];
    const details: any = args[3];

    // Attempt to start
    const callStart = (success: boolean): void => {
      if (success) {
        // Call the start
        this.doStart(mid, userList, activityDefinition, details);
      } else {
        // There must be a currently running activity, and it did not stop. Report failure to the Copilot.
        const traceStr: string = JSON.stringify({
          userList: userList,
          activityDefinition: activityDefinition,
          details: details
        });
        this.onMessageComplete(mid, false, {
          tag: CopilotResponseTags.ERROR,
          message:
            'CopilotService could not stop currently running activity. ' +
            traceStr
        });
      }
    }
    // Stop the currently running activity, if any
    if (this._activityDefinition != null) {
      this.doStop({}, callStart);
    } else {
      callStart(true);
    }
  }

  /**
   * Starts the widget with given parameters
   * @param mid - Message id of the request
   * @param userList - list of user objects with folowing format {uid:string, userState:any}
   * @param activityDefinition - object containing activity data with the folowing format
   *          {daid:string, lid:string, activityData:any}
   * @param details - object containing additional/optional parameters
   */
  private doStart(
    mid: string,
    userList: any[],
    activityDefinition: ActivityDefinition,
    details: any
  ): void {
    const { startCallback, userAddedCallback } = this._copilotProps;
    const traceStr = JSON.stringify({
      userList: userList,
      activityDefinition: activityDefinition,
      details: details
    });

    const callback = (success: boolean, data: any = null): void => {
      this.onMessageComplete(mid, success, data);
    };

    if (
      startCallback != null &&
      userList != null &&
      activityDefinition != null
    ) {
      this.traceExternal('Starting widget with: ' + traceStr);

      //create users

      for (const user of userList) {
        const copilotUser = new CopilotUser(user.uid);

        this._userManager.addUser(copilotUser);
        userAddedCallback((): number => 0, copilotUser);
      }
      this._activityDefinition = activityDefinition;

      startCallback(callback, activityDefinition, details);
    } else {
      this.onMessageComplete(mid, false, {
        tag: CopilotResponseTags.ERROR,
        message:
          'CopilotService is missing the startGameCallback or necessary data. ' +
          traceStr
      });
    }
  }

  /**
   * Stops the widget
   * @param mid - Message id of the request
   * @param details - object containing additiona/optional parameters
   */
  private stopActivity(args: any[]): void {
    const mid: string = args[0];
    const details: any = args[1];

    // Notify the Copilot that the stop completed
    const stopCompleteCallback = (success: boolean, data: any = null): void => {
      this.onMessageComplete(mid, success, data);
    }

    this.doStop(details, stopCompleteCallback);
  }

  /**
   * Stops the widget
   * @param details - object containing additiona/optional parameters
   * @param stopCompleteCallback - callback when stop is complete
   */
  private doStop(
    details: any,
    stopCompleteCallback: (success: boolean) => void
  ): void {
    const func = this._copilotProps.stopCallback;
    if (func != null) {
      this.traceExternal('Stopping activity');
      const callback = (success: boolean): void => {
        // Only clear the users if we have a currently running activity.
        if (this._activityDefinition != null) {
          this.clearUsers();
          this._activityDefinition = null;
        }
        stopCompleteCallback(success);
      };
      func(callback, details);
    } else {
      stopCompleteCallback(false);
    }
  }

  /**
   * Pause or resume this widget
   * @param mid - Message id of the request
   * @param value - true if pausing the widget
   * @param details - object containing additional/optional parameters
   */
  private setPaused(args: any[]): void {
    const mid: string = args[0];
    const value: boolean = args[1];
    const details: any = args[2];
    const func = this._copilotProps.pauseCallback;
    if (func != null) {
      this.traceExternal('Setting pause: ' + value);
      const callback = (success: boolean, data: any = null): void => {
        this.onMessageComplete(mid, success, data);
      };
      func(callback, value, details);
    } else {
      this.onMessageComplete(mid, false, {
        tag: CopilotResponseTags.ERROR,
        message: 'CopilotService is missing the pause/resume callback.'
      });
    }
  }

  /**
   * Add a user. Used after the activity has been started.
   * @param mid - id of this message
   * @param uid - id of the user
   * @param userState - object containing user data
   * @param details - object containing additional/optional parameters
   */
  private addUser(args: any[]): void {
    const mid: string = args[0];
    const uid: string = args[1];

    const callback = (success: boolean, data: any = null): void => {
      this.onMessageComplete(mid, success, data);
    };

    const user: User = new CopilotUser(uid);
    this._userManager.addUser(user);

    this._copilotProps.userAddedCallback(callback, user);
  }

  /**
   * Remove a user. Used after the activity has been started.
   * @param mid - id of this message
   * @param uid - id of the user
   * @param details - object containing additional/optional parameters
   */
  private removeUser(args: any[]): void {
    const mid: string = args[0];
    const uid: string = args[1];
    const user: User = this._userManager.getUserByUserId(uid); //get user
    if (user != null) {
      //call the callback if it exists and then remove the user
      const func = this._copilotProps.userRemovedCallback;
      if (func != null) {
        const callback = (success: boolean, data: any = null): void => {
          this._userManager.removeUserByUserId(uid);
          this.onMessageComplete(mid, success, data);
        };
        func(callback, user);
      } else {
        this._userManager.removeUserByUserId(uid);
        this.onMessageComplete(mid, true, {
          tag: CopilotResponseTags.WARNING,
          message: 'CopilotService is missing the removeUser callback.'
        });
      }
    } else {
      this.onMessageComplete(mid, false, {
        tag: CopilotResponseTags.ERROR,
        message: 'No user to remove'
      });
    }
  }

  /**
   * Process a serialized command
   * @param mid - message id
   * @param command - command name
   * @param args - serialized arguments to be deserialized by the widget
   */
  private commandToWidget(args: any[]): void {
    const mid: string = args[0];
    const cbCommand: string = args[1];
    const cbArgs: string = args[2];
    const func = this._copilotProps.commandToWidgetCallback;
    if (func != null) {
      this.traceExternal(
        'Processing command: ' +
          {
            command: cbCommand,
            args: cbArgs
          }
      );
      const callback = (success: boolean, data: any = null): void => {
        this.onMessageComplete(mid, success, data);
      };
      func(callback, cbCommand, cbArgs);
    } else {
      this.onMessageComplete(mid, false, {
        tag: CopilotResponseTags.ERROR,
        message: 'CopilotService is missing the command callback.'
      });
    }
  }

  /**
   * Removes all users registered in the user manager of this copilot service from the cgsapi.
   */
  private clearUsers(): void {
    this._userManager = new CopilotUserManager();
  }

  /**
   *
   * Copilot Command Callbacks - Users Management
   *
   **/

  /**
   * Checks if the uid is for a known user and of valid fromat for logging
   * @param uid - User ID.
   * @return
   */
  private isUidValid(uid: string): boolean {
    return uid != null && this._userManager.userExistsByUserId(uid);
  }
}
